import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, Plus, Copy, Download, QrCode, Pencil, Trash2, ExternalLink, Users, Store, Megaphone, Share2, Gift, Printer, Loader2, Upload, X, FileText, FileArchive } from "lucide-react";
import QRCode from "qrcode";
import JSZip from "jszip";
import { jsPDF } from "jspdf";
import { drawLabelToCanvas, preloadLogoImage } from "@/lib/label-utils";

interface QRSource {
  id: string;
  code: string;
  name: string;
  type: 'partner' | 'retail' | 'influencer' | 'campaign' | 'referral';
  is_active: boolean;
  contact_name: string | null;
  contact_email: string | null;
  notes: string | null;
  qr_code_data_url: string | null;
  logo_url: string | null;
  scan_count: number;
  conversion_count: number;
  created_at: string;
  next_sequence: number;
}

const SOURCE_TYPES = [
  { value: 'partner', label: 'Partner', icon: Users },
  { value: 'retail', label: 'Retail', icon: Store },
  { value: 'influencer', label: 'Influencer', icon: Share2 },
  { value: 'campaign', label: 'Campaign', icon: Megaphone },
  { value: 'referral', label: 'Referral', icon: Gift },
] as const;

const getTypeIcon = (type: string) => {
  const typeConfig = SOURCE_TYPES.find(t => t.value === type);
  return typeConfig?.icon || Users;
};

export default function AdminQRSources() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [sources, setSources] = useState<QRSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSource, setEditingSource] = useState<QRSource | null>(null);
  const [filterType, setFilterType] = useState<string>('all');
  
  // Label generation state
  const [labelDialogOpen, setLabelDialogOpen] = useState(false);
  const [labelSource, setLabelSource] = useState<QRSource | null>(null);
  const [labelQuantity, setLabelQuantity] = useState<number>(25);
  const [isGeneratingLabels, setIsGeneratingLabels] = useState(false);
  const [labelPreview, setLabelPreview] = useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [exportFormat, setExportFormat] = useState<'zip' | 'pdf'>('pdf');
  
  // Form state
  const [formData, setFormData] = useState<{
    code: string;
    name: string;
    type: 'partner' | 'retail' | 'influencer' | 'campaign' | 'referral';
    is_active: boolean;
    contact_name: string;
    contact_email: string;
    notes: string;
  }>({
    code: '',
    name: '',
    type: 'partner',
    is_active: true,
    contact_name: '',
    contact_email: '',
    notes: '',
  });
  
  // Logo upload state
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);

  const appUrl = window.location.origin;

  useEffect(() => {
    fetchSources();
  }, []);

  const fetchSources = async () => {
    console.log('[QR] Fetching sources...');
    setLoading(true);
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      
      const { data, error } = await supabase
        .from('qr_sources')
        .select('*')
        .order('created_at', { ascending: false })
        .abortSignal(controller.signal);

      clearTimeout(timeoutId);

      if (error) {
        console.error('[QR] Fetch error:', error);
        toast({ title: "Error", description: `Failed to load sources: ${error.message}`, variant: "destructive" });
      } else {
        console.log('[QR] Fetched', data?.length, 'sources');
        setSources((data as unknown as QRSource[]) || []);
      }
    } catch (err: any) {
      console.error('[QR] Fetch exception:', err);
      if (err.name === 'AbortError') {
        toast({ title: "Timeout", description: "Request took too long. Please refresh.", variant: "destructive" });
      } else {
        toast({ title: "Error", description: err.message || "Failed to load sources", variant: "destructive" });
      }
    } finally {
      setLoading(false);
    }
  };

  const generateQRCode = async (code: string): Promise<string> => {
    const url = `${appUrl}/scan/${code}`;
    console.log('[QR] Generating QR code for:', url);
    
    // Wrap in timeout to prevent infinite hang
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error('QR code generation timed out after 10 seconds'));
      }, 10000);
      
      QRCode.toDataURL(url, { 
        width: 512, 
        margin: 2,
        color: { dark: '#000000', light: '#ffffff' }
      })
        .then((result) => {
          clearTimeout(timeoutId);
          console.log('[QR] QR code generated successfully');
          resolve(result);
        })
        .catch((err) => {
          clearTimeout(timeoutId);
          console.error('[QR] QR code generation failed:', err);
          reject(err);
        });
    });
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStep, setSubmitStep] = useState<string>('');

  const handleSubmit = async () => {
    console.log('[QR] handleSubmit called');
    
    if (!formData.code || !formData.name) {
      toast({ title: "Error", description: "Code and name are required", variant: "destructive" });
      return;
    }

    // Sanitize code
    const sanitizedCode = formData.code.toUpperCase().replace(/[^A-Z0-9_-]/g, '');
    if (sanitizedCode.length < 2) {
      toast({ title: "Error", description: "Code must be at least 2 characters", variant: "destructive" });
      return;
    }

    setIsSubmitting(true);
    setSubmitStep('Uploading logo...');
    console.log('[QR] Starting submit...', { sanitizedCode, editing: !!editingSource });

    try {
      // Upload logo if provided
      let logoUrl: string | null = editingSource?.logo_url || null;
      
      if (logoFile) {
        setIsUploadingLogo(true);
        const fileExt = logoFile.name.split('.').pop();
        const fileName = `${sanitizedCode}-${Date.now()}.${fileExt}`;
        
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('partner-logos')
          .upload(fileName, logoFile, { upsert: true });
        
        if (uploadError) {
          console.error('[QR] Logo upload error:', uploadError);
          toast({ title: "Logo Upload Failed", description: uploadError.message, variant: "destructive" });
          return;
        }
        
        const { data: { publicUrl } } = supabase.storage
          .from('partner-logos')
          .getPublicUrl(fileName);
        
        logoUrl = publicUrl;
        setIsUploadingLogo(false);
      }
      
      setSubmitStep('Generating QR code...');
      // Generate QR code with timeout
      let qrCodeDataUrl: string;
      try {
        qrCodeDataUrl = await generateQRCode(sanitizedCode);
      } catch (qrError: any) {
        console.error('[QR] QR generation failed:', qrError);
        toast({ title: "QR Generation Failed", description: qrError.message, variant: "destructive" });
        return;
      }

      setSubmitStep('Saving to database...');

      if (editingSource) {
        // Update existing
        console.log('[QR] Updating existing source:', editingSource.id);
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000);
        
        const { error } = await supabase
          .from('qr_sources')
          .update({
            code: sanitizedCode,
            name: formData.name,
            type: formData.type,
            is_active: formData.is_active,
            contact_name: formData.contact_name || null,
            contact_email: formData.contact_email || null,
            notes: formData.notes || null,
            qr_code_data_url: qrCodeDataUrl,
            logo_url: logoUrl,
          })
          .eq('id', editingSource.id)
          .abortSignal(controller.signal);

        clearTimeout(timeoutId);

        if (error) {
          console.error('[QR] Update error:', error);
          throw error;
        }
        console.log('[QR] Update successful');
        toast({ title: "Success", description: "Source updated" });
      } else {
        // Create new
        console.log('[QR] Creating new source');
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000);
        
        const { data, error } = await supabase
          .from('qr_sources')
          .insert({
            code: sanitizedCode,
            name: formData.name,
            type: formData.type,
            is_active: formData.is_active,
            contact_name: formData.contact_name || null,
            contact_email: formData.contact_email || null,
            notes: formData.notes || null,
            qr_code_data_url: qrCodeDataUrl,
            logo_url: logoUrl,
          })
          .select()
          .abortSignal(controller.signal);

        clearTimeout(timeoutId);

        console.log('[QR] Insert result:', { data, error });

        if (error) {
          if (error.code === '23505') {
            toast({ title: "Error", description: "A source with this code already exists", variant: "destructive" });
            return;
          }
          throw error;
        }
        console.log('[QR] Insert successful');
        toast({ title: "Success", description: "Source created" });
      }

      setDialogOpen(false);
      resetForm();
      fetchSources();
    } catch (error: any) {
      console.error('[QR] Submit error:', error);
      if (error.name === 'AbortError') {
        toast({ title: "Timeout", description: "Database request took too long. Please try again.", variant: "destructive" });
      } else {
        toast({ title: "Error", description: error.message || "Failed to save source", variant: "destructive" });
      }
    } finally {
      setIsSubmitting(false);
      setSubmitStep('');
      setIsUploadingLogo(false);
    }
  };

  const handleDelete = async (source: QRSource) => {
    if (!confirm(`Delete source "${source.name}"?`)) return;

    const { error } = await supabase
      .from('qr_sources')
      .delete()
      .eq('id', source.id);

    if (error) {
      toast({ title: "Error", description: "Failed to delete source", variant: "destructive" });
    } else {
      toast({ title: "Success", description: "Source deleted" });
      fetchSources();
    }
  };

  const toggleActive = async (source: QRSource) => {
    const { error } = await supabase
      .from('qr_sources')
      .update({ is_active: !source.is_active })
      .eq('id', source.id);

    if (error) {
      toast({ title: "Error", description: "Failed to update source", variant: "destructive" });
    } else {
      fetchSources();
    }
  };

  const copyUrl = (code: string) => {
    const url = `${appUrl}/scan/${code}`;
    navigator.clipboard.writeText(url);
    toast({ title: "Copied!", description: "URL copied to clipboard" });
  };

  const downloadQR = (source: QRSource) => {
    if (!source.qr_code_data_url) {
      toast({ title: "Error", description: "QR code not generated", variant: "destructive" });
      return;
    }
    
    const link = document.createElement('a');
    link.download = `qr-${source.code}.png`;
    link.href = source.qr_code_data_url;
    link.click();
  };

  const generatePreview = async (source: QRSource) => {
    const previewStart = performance.now();
    console.log('[Label Preview] Starting preview generation for source:', source.code);
    
    setIsLoadingPreview(true);
    setLabelPreview(null);
    try {
      const seq = source.next_sequence || 1;
      const boxNumber = `${source.code}-${String(seq).padStart(3, '0')}`;
      const qrUrl = `${appUrl}/qr/${boxNumber}`;
      console.log('[Label Preview] Box number:', boxNumber);
      console.log('[Label Preview] QR URL:', qrUrl);
      
      console.log('[Label Preview] Generating QR code...');
      const qrStart = performance.now();
      const qrDataUrl = await QRCode.toDataURL(qrUrl, {
        width: 512,
        margin: 2,
        color: { dark: '#000000', light: '#ffffff' }
      });
      console.log(`[Label Preview] QR code generated in ${(performance.now() - qrStart).toFixed(2)}ms`);
      
      // Pre-load logo if available
      const logoImg = source.logo_url ? await preloadLogoImage(source.logo_url) : null;
      
      console.log('[Label Preview] Drawing label to canvas...');
      const drawStart = performance.now();
      const preview = await drawLabelToCanvas(boxNumber, qrDataUrl, logoImg);
      console.log(`[Label Preview] Label drawn in ${(performance.now() - drawStart).toFixed(2)}ms`);
      
      setLabelPreview(preview);
      console.log(`[Label Preview] Preview generation complete in ${(performance.now() - previewStart).toFixed(2)}ms`);
    } catch (error) {
      console.error('[Label Preview] Preview generation failed:', error);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const openLabelDialog = (source: QRSource) => {
    setLabelSource(source);
    setLabelQuantity(25);
    setLabelPreview(null);
    setLabelDialogOpen(true);
    generatePreview(source);
  };

  // drawLabelToCanvas is now imported from @/lib/label-utils

  const generateLabels = async () => {
    if (!labelSource) {
      console.log('[Label Gen] No label source selected, aborting');
      return;
    }
    
    const genStart = performance.now();
    console.log('[Label Gen] ========== Starting batch generation ==========');
    console.log('[Label Gen] Source:', labelSource.code);
    console.log('[Label Gen] Quantity:', labelQuantity);
    console.log('[Label Gen] Export format:', exportFormat);
    
    setIsGeneratingLabels(true);
    
    try {
      const currentSeq = labelSource.next_sequence || 1;
      console.log('[Label Gen] Starting sequence:', currentSeq);
      
      // Pre-load logo once for all labels
      console.log('[Label Gen] Pre-loading logo...');
      const logoImg = labelSource.logo_url ? await preloadLogoImage(labelSource.logo_url) : null;
      console.log('[Label Gen] Logo preloaded:', logoImg ? 'success' : 'skipped');
      
      const labels: { code: string; labelDataUrl: string }[] = [];
      
      // Generate full labels in parallel batches
      console.log('[Label Gen] Beginning parallel batch label generation...');
      const loopStart = performance.now();
      
      const BATCH_SIZE = 5;
      for (let batchStart = 0; batchStart < labelQuantity; batchStart += BATCH_SIZE) {
        const batchEnd = Math.min(batchStart + BATCH_SIZE, labelQuantity);
        console.log(`[Label Gen] Processing batch ${Math.floor(batchStart / BATCH_SIZE) + 1}/${Math.ceil(labelQuantity / BATCH_SIZE)}`);
        
        const batchPromises = [];
        for (let i = batchStart; i < batchEnd; i++) {
          const seq = currentSeq + i;
          const boxNumber = `${labelSource.code}-${String(seq).padStart(3, '0')}`;
          const qrUrl = `${appUrl}/qr/${boxNumber}`;
          
          batchPromises.push(
            (async () => {
              const qrDataUrl = await QRCode.toDataURL(qrUrl, {
                width: 512,
                margin: 2,
                color: { dark: '#000000', light: '#ffffff' }
              });
              const labelDataUrl = await drawLabelToCanvas(boxNumber, qrDataUrl, logoImg);
              return { code: boxNumber, labelDataUrl };
            })()
          );
        }
        
        const batchResults = await Promise.all(batchPromises);
        labels.push(...batchResults);
      }
      
      console.log(`[Label Gen] All ${labels.length} labels generated in ${(performance.now() - loopStart).toFixed(2)}ms`);
      
      // Update next_sequence in database
      const newNextSeq = currentSeq + labelQuantity;
      console.log('[Label Gen] Updating sequence in database to:', newNextSeq);
      const dbStart = performance.now();
      
      const { error: updateError } = await supabase
        .from('qr_sources')
        .update({ next_sequence: newNextSeq })
        .eq('id', labelSource.id);
      
      if (updateError) {
        console.error('[Label Gen] Database update failed:', updateError);
        throw new Error('Failed to update sequence counter');
      }
      console.log(`[Label Gen] Database updated in ${(performance.now() - dbStart).toFixed(2)}ms`);
      
      if (exportFormat === 'pdf') {
        console.log('[Label Gen] Creating PDF with Avery 5163 layout...');
        const pdfStart = performance.now();
        
        // Generate PDF with Avery 5163 layout (2 columns x 5 rows = 10 labels per sheet)
        // Sheet: 8.5" x 11", Label: 4" x 2" (landscape), Margins per Avery specs
        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'in',
          format: 'letter'
        });
        
        const COLS = 2;
        const ROWS = 5;
        const LABELS_PER_PAGE = COLS * ROWS;
        const LABEL_WIDTH = 4; // inches (landscape)
        const LABEL_HEIGHT = 2; // inches
        const MARGIN_LEFT = 0.15625; // Avery 5163 spec
        const MARGIN_TOP = 0.5;
        const GAP_X = 0.1875; // Gap between columns
        const GAP_Y = 0; // No gap between rows
        
        const pagesNeeded = Math.ceil(labels.length / LABELS_PER_PAGE);
        console.log('[Label Gen] Pages needed:', pagesNeeded);
        
        for (let i = 0; i < labels.length; i++) {
          const positionOnPage = i % LABELS_PER_PAGE;
          const col = positionOnPage % COLS;
          const row = Math.floor(positionOnPage / COLS);
          const pageNum = Math.floor(i / LABELS_PER_PAGE) + 1;
          
          // Add new page if needed (except for first label)
          if (i > 0 && positionOnPage === 0) {
            console.log(`[Label Gen] Adding new page ${pageNum}`);
            pdf.addPage();
          }
          
          const x = MARGIN_LEFT + col * (LABEL_WIDTH + GAP_X);
          const y = MARGIN_TOP + row * LABEL_HEIGHT;
          
          console.log(`[Label Gen] Adding label ${i + 1} to PDF at page ${pageNum}, col ${col}, row ${row}, position (${x.toFixed(3)}", ${y.toFixed(3)}")`);
          
          // Add label image (already landscape 4x2)
          pdf.addImage(labels[i].labelDataUrl, 'PNG', x, y, LABEL_WIDTH, LABEL_HEIGHT);
        }
        
        console.log(`[Label Gen] PDF created in ${(performance.now() - pdfStart).toFixed(2)}ms`);
        console.log('[Label Gen] Initiating PDF download...');
        pdf.save(`${labelSource.code}-labels-${currentSeq}-to-${newNextSeq - 1}.pdf`);
        console.log('[Label Gen] PDF download initiated');
      } else {
        console.log('[Label Gen] Creating ZIP archive...');
        const zipStart = performance.now();
        
        // Create ZIP file with individual PNGs
        const zip = new JSZip();
        
        for (const { code, labelDataUrl } of labels) {
          const base64 = labelDataUrl.split(',')[1];
          zip.file(`${code}.png`, base64, { base64: true });
          console.log(`[Label Gen] Added ${code}.png to ZIP`);
        }
        
        console.log('[Label Gen] Generating ZIP blob...');
        const blob = await zip.generateAsync({ type: 'blob' });
        console.log(`[Label Gen] ZIP created in ${(performance.now() - zipStart).toFixed(2)}ms, size: ${(blob.size / 1024).toFixed(2)}KB`);
        
        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = `${labelSource.code}-labels-${currentSeq}-to-${newNextSeq - 1}.zip`;
        link.href = downloadUrl;
        console.log('[Label Gen] Initiating ZIP download...');
        link.click();
        URL.revokeObjectURL(downloadUrl);
        console.log('[Label Gen] ZIP download initiated');
      }
      
      // Update local state
      setSources(prev => prev.map(s => 
        s.id === labelSource.id 
          ? { ...s, next_sequence: newNextSeq }
          : s
      ));
      
      const formatLabel = exportFormat === 'pdf' ? 'PDF' : 'ZIP';
      console.log(`[Label Gen] ========== Generation complete in ${(performance.now() - genStart).toFixed(2)}ms ==========`);
      
      toast({
        title: "Labels Generated!",
        description: `Downloaded ${labelQuantity} labels as ${formatLabel} (${labelSource.code}-${String(currentSeq).padStart(3, '0')} to ${labelSource.code}-${String(newNextSeq - 1).padStart(3, '0')})`,
      });
      
      setLabelDialogOpen(false);
    } catch (error: any) {
      console.error('[Label Gen] ========== Generation FAILED ==========');
      console.error('[Label Gen] Error:', error);
      console.error('[Label Gen] Error message:', error.message);
      console.error('[Label Gen] Error stack:', error.stack);
      toast({
        title: "Generation Failed",
        description: error.message || "Failed to generate labels",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingLabels(false);
      console.log('[Label Gen] Generation state reset');
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      type: 'partner',
      is_active: true,
      contact_name: '',
      contact_email: '',
      notes: '',
    });
    setEditingSource(null);
    setLogoFile(null);
    setLogoPreview(null);
  };

  const openEditDialog = (source: QRSource) => {
    setEditingSource(source);
    setFormData({
      code: source.code,
      name: source.name,
      type: source.type,
      is_active: source.is_active,
      contact_name: source.contact_name || '',
      contact_email: source.contact_email || '',
      notes: source.notes || '',
    });
    setLogoFile(null);
    setLogoPreview(source.logo_url || null);
    setDialogOpen(true);
  };
  
  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast({ title: "File too large", description: "Logo must be under 2MB", variant: "destructive" });
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setLogoPreview(e.target?.result as string);
      reader.readAsDataURL(file);
    }
  };
  
  const removeLogo = async () => {
    setLogoFile(null);
    setLogoPreview(null);
    
    // If editing and there was an existing logo, remove it from database
    if (editingSource?.logo_url) {
      const { error } = await supabase
        .from('qr_sources')
        .update({ logo_url: null })
        .eq('id', editingSource.id);
      
      if (error) {
        toast({ title: "Error", description: "Failed to remove logo", variant: "destructive" });
      } else {
        toast({ title: "Logo removed" });
        fetchSources();
      }
    }
  };

  const filteredSources = filterType === 'all' 
    ? sources 
    : sources.filter(s => s.type === filterType);

  const getConversionRate = (source: QRSource) => {
    if (source.scan_count === 0) return '0%';
    return `${((source.conversion_count / source.scan_count) * 100).toFixed(1)}%`;
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-2">QR Source Management</h1>
            <p className="text-muted-foreground">Create and manage QR codes for partners, campaigns, and more</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Add Source
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{editingSource ? 'Edit Source' : 'Create New Source'}</DialogTitle>
                <DialogDescription>
                  Create a QR code source for tracking scans and conversions
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="code">Code *</Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="ABC-MOVERS"
                    className="mt-1"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    URL: {appUrl}/scan/{formData.code || 'CODE'}
                  </p>
                </div>
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="ABC Moving Company"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="type">Type</Label>
                  <Select 
                    value={formData.type} 
                    onValueChange={(value: any) => setFormData({ ...formData, type: value })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SOURCE_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <type.icon className="h-4 w-4" />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="is_active">Active</Label>
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                </div>
                <div>
                  <Label htmlFor="contact_name">Contact Name</Label>
                  <Input
                    id="contact_name"
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    placeholder="John Smith"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="contact_email">Contact Email</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    placeholder="john@example.com"
                    className="mt-1"
                  />
                </div>
                {/* Partner Logo Upload */}
                <div>
                  <Label>Partner Logo (optional)</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Will appear at top of printed labels
                  </p>
                  {logoPreview ? (
                    <div className="relative inline-block">
                      <img 
                        src={logoPreview} 
                        alt="Logo preview" 
                        className="h-20 max-w-full object-contain border rounded-md"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute -top-2 -right-2 h-6 w-6"
                        onClick={removeLogo}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
                        className="max-w-[200px]"
                      />
                    </div>
                  )}
                </div>
                <div>
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Internal notes about this source..."
                    className="mt-1"
                    rows={3}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => { setDialogOpen(false); resetForm(); }} 
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button 
                  type="button" 
                  onClick={() => {
                    console.log('[QR] Create button clicked');
                    handleSubmit();
                  }} 
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (submitStep || 'Saving...') : (editingSource ? 'Update' : 'Create')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{sources.length}</div>
              <p className="text-sm text-muted-foreground">Total Sources</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{sources.filter(s => s.is_active).length}</div>
              <p className="text-sm text-muted-foreground">Active</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{sources.reduce((acc, s) => acc + s.scan_count, 0)}</div>
              <p className="text-sm text-muted-foreground">Total Scans</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{sources.reduce((acc, s) => acc + s.conversion_count, 0)}</div>
              <p className="text-sm text-muted-foreground">Total Conversions</p>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-4 mb-6">
          <Label>Filter by type:</Label>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {SOURCE_TYPES.map(type => (
                <SelectItem key={type.value} value={type.value}>
                  <div className="flex items-center gap-2">
                    <type.icon className="h-4 w-4" />
                    {type.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Sources Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Source</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Scans</TableHead>
                  <TableHead className="text-right">Conversions</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : filteredSources.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No sources found. Create one to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSources.map((source) => {
                    const TypeIcon = getTypeIcon(source.type);
                    return (
                      <TableRow key={source.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{source.name}</div>
                            <code className="text-xs text-muted-foreground">{source.code}</code>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="flex items-center gap-1 w-fit">
                            <TypeIcon className="h-3 w-3" />
                            {source.type}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={source.is_active}
                            onCheckedChange={() => toggleActive(source)}
                          />
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {source.scan_count}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {source.conversion_count}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge variant={source.conversion_count > 0 ? "default" : "secondary"}>
                            {getConversionRate(source)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openLabelDialog(source)}
                              title="Generate Labels"
                              className="text-primary hover:text-primary"
                            >
                              <Printer className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => copyUrl(source.code)}
                              title="Copy URL"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => downloadQR(source)}
                              title="Download QR"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => window.open(`/scan/${source.code}`, '_blank')}
                              title="Test Link"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditDialog(source)}
                              title="Edit"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(source)}
                              title="Delete"
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <div className="mt-8">
          <Button variant="ghost" onClick={() => navigate('/admin')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Admin
          </Button>
        </div>

        {/* Label Generation Dialog */}
        <Dialog open={labelDialogOpen} onOpenChange={setLabelDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Generate Partner Labels</DialogTitle>
              <DialogDescription>
                Create printable QR code labels for {labelSource?.name}
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
              {/* Label Preview */}
              <div className="bg-muted rounded-lg p-4 flex items-center justify-center min-h-[400px]">
                {isLoadingPreview ? (
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-8 w-8 animate-spin" />
                    <span className="text-sm">Generating preview...</span>
                  </div>
                ) : labelPreview ? (
                  <img 
                    src={labelPreview} 
                    alt="Label Preview" 
                    className="max-w-full h-auto shadow-md border border-border rounded"
                    style={{ aspectRatio: '2/1' }}
                  />
                ) : (
                  <p className="text-muted-foreground text-sm">Preview not available</p>
                )}
              </div>

              {/* Controls */}
              <div className="space-y-4">
                <div>
                  <Label>Quantity</Label>
                  <Select 
                    value={String(labelQuantity)} 
                    onValueChange={(v) => setLabelQuantity(Number(v))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="25">25 labels</SelectItem>
                      <SelectItem value="50">50 labels</SelectItem>
                      <SelectItem value="100">100 labels</SelectItem>
                      <SelectItem value="250">250 labels</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Export Format</Label>
                  <Select 
                    value={exportFormat} 
                    onValueChange={(v) => setExportFormat(v as 'zip' | 'pdf')}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pdf">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          <span>PDF (Avery 5163 sheets)</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="zip">
                        <div className="flex items-center gap-2">
                          <FileArchive className="h-4 w-4" />
                          <span>ZIP (individual PNGs)</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {labelSource && (
                  <div className="bg-muted rounded-lg p-3 text-sm">
                    <p className="font-medium mb-1">Range:</p>
                    <p className="text-muted-foreground">
                      {labelSource.code}-{String(labelSource.next_sequence || 1).padStart(3, '0')} to{' '}
                      {labelSource.code}-{String((labelSource.next_sequence || 1) + labelQuantity - 1).padStart(3, '0')}
                    </p>
                    {exportFormat === 'pdf' && (
                      <p className="text-muted-foreground mt-1">
                        {Math.ceil(labelQuantity / 10)} page{Math.ceil(labelQuantity / 10) > 1 ? 's' : ''} (10 labels per sheet)
                      </p>
                    )}
                  </div>
                )}
                <div className="text-xs text-muted-foreground">
                  {exportFormat === 'pdf' ? (
                    <p>PDF formatted for Avery 5163/8163 sheets (2×5 layout, 10 labels per page).</p>
                  ) : (
                    <p>Individual 2" × 4" PNG files at 300 DPI.</p>
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setLabelDialogOpen(false)} disabled={isGeneratingLabels}>
                Cancel
              </Button>
              <Button onClick={generateLabels} disabled={isGeneratingLabels}>
                {isGeneratingLabels ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Generate & Download
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
